import type { MutableRefObject, ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import { css } from 'styled-components';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import type { StyleProduct } from '@storefront/lib-global/codegen/types';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { PdpArrowCircleLeft, PdpArrowCircleRight } from '@storefront/lib-global/icons';
import { colours, media } from '@storefront/lib-global/stylings';
import {
  CardSelectorArrowButton,
  CardSelectorArrowButtonWrapper,
  CardSelectorCarousel,
  CardSelectorContainer,
  CardSelectorHeader,
  CardSelectorHeaderText,
  CardSelectorImageList,
} from './CardSelector.styles';

export const selectedCardStyle = css`
  padding: 0;
  pointer-events: none;

  &::before {
    background: ${colours.TURQUOISE};
    bottom: 1px;
    content: '';
    height: 6px;
    position: absolute;
    width: 73px;
    z-index: 1;
  }
`;

export type CardSelectorProps = {
  headerText: string;
  colors?: StyleProduct[];
  children: ReactNode;
  swiperRef: MutableRefObject<SwiperType | undefined>;
};

export function CardSelector({ headerText, children, swiperRef, colors }: CardSelectorProps) {
  const { t } = useTranslation(['lib-products-common']);
  const isDesktop = useMediaMatch(media.greaterThan('lg'));

  return (
    <CardSelectorContainer>
      <CardSelectorHeader aria-label={`${t('pdp.colours.available', { count: colors?.length })}`}>
        <CardSelectorHeaderText>{headerText}</CardSelectorHeaderText>
        <CardSelectorArrowButtonWrapper>
          <CardSelectorArrowButton type="button" className="card-selector-prev">
            <PdpArrowCircleLeft aria-label="Previous button" />
          </CardSelectorArrowButton>
          <CardSelectorArrowButton type="button" className="card-selector-next">
            <PdpArrowCircleRight aria-label="Next button" />
          </CardSelectorArrowButton>
        </CardSelectorArrowButtonWrapper>
      </CardSelectorHeader>
      <CardSelectorImageList>
        <CardSelectorCarousel
          modules={[Navigation]}
          onBeforeInit={(swiper) => {
            // eslint-disable-next-line no-param-reassign
            swiperRef.current = swiper;
          }}
          navigation={{
            nextEl: '.card-selector-next',
            prevEl: '.card-selector-prev',
          }}
          slidesPerView="auto"
          slidesPerGroup={3}
          spaceBetween={8}
          watchOverflow
          slideToClickedSlide
          noSwiping={!!isDesktop}
          noSwipingClass="card-selector-slide"
        >
          {children}
        </CardSelectorCarousel>
      </CardSelectorImageList>
    </CardSelectorContainer>
  );
}

export default CardSelector;
